import { Loader2Icon, CheckIcon, XIcon } from 'lucide-react';

import { ThumbsDownIcon } from './ui/thumbs-down-icon';
import { ThumbsUpIcon } from './ui/thumbs-up-icon';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export type ReviewEvent = 'APPROVE' | 'REQUEST_CHANGES';

export interface ReviewFormProps {
  reviewEvent: ReviewEvent;
  onEventChange: (event: ReviewEvent) => void;
  onBodyChange: (body: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function ReviewForm({ reviewEvent, onEventChange, onBodyChange, onSubmit, isSubmitting }: ReviewFormProps) {
  const popoverColorClass = cn(
    reviewEvent === 'APPROVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
  );

  return (
    <div className={cn('p-3', popoverColorClass)}>
      <div className="flex flex-col gap-4">
        <RadioGroup value={reviewEvent} onValueChange={(value) => onEventChange(value as ReviewEvent)}>
          <div className="grid grid-cols-2 gap-3">
            <Label
              htmlFor="approve"
              className={cn(
                'radio-tile flex-row justify-center items-center gap-2',
                reviewEvent === 'APPROVE'
                  ? 'radio-tile-selected-emerald'
                  : 'radio-tile-unselected'
              )}
            >
              <RadioGroupItem value="APPROVE" id="approve" className="sr-only" />
              <ThumbsUpIcon className="size-4" />
              <span className="font-medium">Approve</span>
            </Label>

            <Label
              htmlFor="request-changes"
              className={cn(
                'radio-tile flex-row justify-center items-center gap-2',
                reviewEvent === 'REQUEST_CHANGES'
                  ? 'radio-tile-selected-orange'
                  : 'radio-tile-unselected'
              )}
            >
              <RadioGroupItem value="REQUEST_CHANGES" id="request-changes" className="sr-only" />
              <ThumbsDownIcon className="size-4" />
              <div>
                <span className="font-medium">Request</span>
                <br />
                <span className="font-medium">Changes</span>
              </div>
            </Label>
          </div>
        </RadioGroup>

        <Textarea
          placeholder="Leave a review..."
          className="border border-border shadow-none bg-background placeholder:text-muted-foreground text-foreground h-32"
          autoFocus
          onChange={(e) => onBodyChange(e.target.value)}
        />

        <Button
          variant="outline"
          size="sm"
          className="w-full hover:cursor-pointer bg-background"
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          {isSubmitting ? (
            <>
              <Loader2Icon className="size-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </div>
  );
}
